import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from 'recharts';
import { api } from '../../services/api';
import { fmtMoney, fmtHours } from './format';
import { c, t, chart } from '../../theme';

// ── The trend, for everything in scope ───────────────────────────────────────
//
// Every target view in the enhancement pack leads with a trend chart, and the
// only one this portal could draw was per-cost-center. This is the same shape
// across whatever the viewer is allowed to see.
//
// The grain is the level, not a setting: L0 asks "are we where we committed to
// be" of a month, L1 asks "did we make the week" of a pay period. Drawing L0
// at weekly grain hands an executive payroll noise to compare against a monthly
// budget, which is how a normal fortnight becomes an agenda item.
//
// The bucket the page is currently totalling is emphasized, so the KPI tiles
// above always have a visible home in the series underneath them.
//
// A month opens PER PAY RUN, not as a raw total. Payroll is weekly, so a month
// is four runs or five, and a five-run month (May, August 2026) reads a quarter
// higher than its neighbours on identical behavior -- the bar an executive asks
// about is then the calendar, not the overtime. The total stays one click away
// because it is the real money; it is just not the reading the chart leads with.
// A pay-period bar is already one run, so the toggle only exists at month grain.
const MEASURES = [
  { key: 'per_run', label: 'Per pay run' },
  { key: 'total',   label: 'Month total' },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// 'YYYY-MM' -> 'Jul 2026'   |   'YYYY-MM-DD' -> 'Jul 28'
function labelFor(bucket, grain) {
  if (!bucket) return '';
  if (grain === 'month') {
    const [y, m] = bucket.split('-');
    return `${MONTHS[Number(m) - 1] || m} ${y}`;
  }
  const d = new Date(`${bucket}T00:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function OvertimeTrend({ grain, filterQ = '', activeBucket, onSelectBucket }) {
  // One filter fragment rather than a prop per dimension: lens, division and
  // labor category all narrow the same series, and threading them separately
  // meant every new filter touched every panel.
  const limit = grain === 'month' ? 13 : 12;
  const [measure, setMeasure] = useState('per_run');
  const perRun = grain === 'month' && measure === 'per_run';

  const { data, isLoading, error } = useQuery({
    queryKey: ['ot-trend-all', grain, filterQ],
    queryFn:  () => api.get(`/overtime/trend?grain=${grain}&limit=${limit}${filterQ}`),
  });

  const points = data?.points || [];
  // `value` is what the bar draws. A month the calendar holds no run count for
  // cannot be divided, so it draws nothing rather than a total dressed as a
  // per-run figure.
  const rows   = points.map((p) => ({
    ...p,
    label: labelFor(p.bucket, grain),
    value: perRun ? (p.period_count ? p.ot_amount / p.period_count : null) : p.ot_amount,
  }));

  const heading = grain === 'month'
    ? `Overtime cost by month${perRun ? ', average per pay run' : ''} — last ${rows.length || limit}`
    : `Overtime cost by pay period — last ${rows.length || limit}`;

  return (
    <>
      <div style={s.head}>
        <div style={s.title}>{heading}</div>
        {grain === 'month' && (
          <div style={s.seg}>
            {MEASURES.map((m) => (
              <button key={m.key} type="button" onClick={() => setMeasure(m.key)}
                      style={{ ...s.segBtn, ...(m.key === measure ? s.segBtnOn : null) }}>
                {m.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div style={s.card}>
        {isLoading ? (
          <div style={s.empty}>Loading…</div>
        ) : error ? (
          <div style={s.empty}>{String(error.message || error)}</div>
        ) : !rows.length ? (
          <div style={s.empty}>No overtime loaded yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
              <CartesianGrid vertical={false} stroke={chart.grid} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: chart.axisText }}
                     axisLine={false} tickLine={false} />
              <YAxis tickFormatter={fmtMoney} tick={{ fontSize: 10, fill: chart.axisText }}
                     axisLine={false} tickLine={false} width={64} />
              <Tooltip
                cursor={{ fill: 'rgba(27,42,74,0.05)' }}
                content={<TrendTip grain={grain} perRun={perRun} />}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}
                   cursor={onSelectBucket ? 'pointer' : 'default'}
                   onClick={onSelectBucket ? (d) => onSelectBucket(d.bucket) : undefined}>
                {rows.map((p) => (
                  <Cell key={p.bucket}
                        fill={activeBucket && p.bucket === activeBucket ? chart.emphasis : chart.recessive} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </>
  );
}

// Both figures in the tooltip whichever one the bar draws, with the run count
// between them -- the count is the whole reason the two differ.
function TrendTip({ active, payload, label, grain, perRun }) {
  const p = active && payload?.[0]?.payload;
  if (!p) return null;
  const runs = p.period_count;
  return (
    <div style={s.tip}>
      <div style={s.tipHead}>{label}</div>
      {grain === 'month' && runs ? (
        <>
          <div style={perRun ? s.tipStrong : null}>
            {fmtMoney(p.ot_amount / runs)} per pay run
          </div>
          <div style={perRun ? null : s.tipStrong}>
            {fmtMoney(p.ot_amount)} over {runs} pay run{runs === 1 ? '' : 's'}
          </div>
        </>
      ) : (
        <div style={s.tipStrong}>{fmtMoney(p.ot_amount)}</div>
      )}
      <div style={s.tipMuted}>{fmtHours(p.ot_hours)} OT hours</div>
    </div>
  );
}

const s = {
  head:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between',
           gap: '10px', flexWrap: 'wrap', margin: '26px 0 10px' },
  title: { fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
           color: c.textMuted },
  seg:      { display: 'inline-flex', background: c.surfaceSunk, border: `1px solid ${c.border}`,
              borderRadius: '7px', padding: '2px', gap: '2px' },
  segBtn:   { ...t.bodySm, border: 'none', background: 'transparent', color: c.textMuted,
              padding: '4px 9px', borderRadius: '5px', cursor: 'pointer', whiteSpace: 'nowrap' },
  segBtnOn: { background: c.surface, color: c.text, fontWeight: 600,
              boxShadow: '0 1px 2px rgba(17,24,39,0.08)' },
  tip:       { ...t.bodySm, background: c.surface, border: `1px solid ${chart.tooltipBorder}`,
               borderRadius: '8px', padding: '8px 10px', color: c.text },
  tipHead:   { fontWeight: 600, marginBottom: '3px' },
  tipStrong: { fontWeight: 600 },
  tipMuted:  { color: c.textMuted, marginTop: '2px' },
  card:  { background: c.surface, border: `1px solid ${c.border}`, borderRadius: '12px',
           padding: '14px 10px 6px' },
  empty: { textAlign: 'center', padding: '28px', color: c.textFaint },
};
